/**
 * @file locales.ts
 * @description Internationalization locale definitions and country helpers for TableGreet Signage.
 */

import { type Language, SUPPORTED_LANGUAGES } from './i18n';

export type PublicLocale = string;

export const DEFAULT_PUBLIC_LOCALE: PublicLocale = 'en-ma';

export const COUNTRY_CODES: Record<string, string> = {
  Morocco: 'ma',
  'United Arab Emirates': 'ae',
  'Saudi Arabia': 'sa',
  Egypt: 'eg',
  Qatar: 'qa',
  Kuwait: 'kw',
  Bahrain: 'bh',
  Oman: 'om',
  Jordan: 'jo',
  Algeria: 'dz',
  Tunisia: 'tn',
  Brazil: 'br',
  Mexico: 'mx',
  Argentina: 'ar',
  Colombia: 'co',
  Chile: 'cl',
  Peru: 'pe',
  Uruguay: 'uy',
  'United States': 'us',
  'United Kingdom': 'gb',
  France: 'fr',
  Spain: 'es',
  Germany: 'de',
  Italy: 'it',
  Canada: 'ca',
};

export const COUNTRY_DEFAULT_LOCALES: Record<string, { defaultLocale: PublicLocale; enabledLocales: PublicLocale[] }> = {
  Morocco: { defaultLocale: 'fr-ma', enabledLocales: ['fr-ma', 'ar-ma', 'en-ma'] },
  'United Arab Emirates': { defaultLocale: 'en-ae', enabledLocales: ['en-ae', 'ar-ae'] },
  'Saudi Arabia': { defaultLocale: 'ar-sa', enabledLocales: ['ar-sa', 'en-sa'] },
  Egypt: { defaultLocale: 'ar-eg', enabledLocales: ['ar-eg', 'en-eg'] },
  Qatar: { defaultLocale: 'ar-qa', enabledLocales: ['ar-qa', 'en-qa'] },
  Kuwait: { defaultLocale: 'ar-kw', enabledLocales: ['ar-kw', 'en-kw'] },
  Bahrain: { defaultLocale: 'ar-bh', enabledLocales: ['ar-bh', 'en-bh'] },
  Oman: { defaultLocale: 'ar-om', enabledLocales: ['ar-om', 'en-om'] },
  Jordan: { defaultLocale: 'ar-jo', enabledLocales: ['ar-jo', 'en-jo'] },
  Algeria: { defaultLocale: 'fr-dz', enabledLocales: ['fr-dz', 'ar-dz', 'en-dz'] },
  Tunisia: { defaultLocale: 'fr-tn', enabledLocales: ['fr-tn', 'ar-tn', 'en-tn'] },
  'United States': { defaultLocale: 'en-us', enabledLocales: ['en-us', 'es-us'] },
  'United Kingdom': { defaultLocale: 'en-gb', enabledLocales: ['en-gb'] },
  France: { defaultLocale: 'fr-fr', enabledLocales: ['fr-fr', 'en-fr'] },
  Spain: { defaultLocale: 'es-es', enabledLocales: ['es-es', 'en-es'] },
  Germany: { defaultLocale: 'de-de', enabledLocales: ['de-de', 'en-de'] },
  Italy: { defaultLocale: 'it-it', enabledLocales: ['it-it', 'en-it'] },
  Canada: { defaultLocale: 'en-ca', enabledLocales: ['en-ca', 'fr-ca'] },
};

export const getCountryCode = (countryName?: string | null): string => {
  if (!countryName) return 'ma';
  return COUNTRY_CODES[countryName] || 'ma';
};

export const isPublicLocale = (value: string | null | undefined): value is PublicLocale => {
  if (!value) return false;
  const parts = value.toLowerCase().split('-');
  if (parts.length !== 2) return false;
  const [lang, country] = parts;
  return SUPPORTED_LANGUAGES.some(l => l.code === lang) && country.length === 2;
};

export const normalizePublicLocale = (value: string | null | undefined): PublicLocale => {
  if (!value) return DEFAULT_PUBLIC_LOCALE;
  const normalized = value.toLowerCase() === 'en-uk' ? 'en-gb' : value.toLowerCase();
  return isPublicLocale(normalized) ? normalized : DEFAULT_PUBLIC_LOCALE;
};

export const getCountryDefaultLocales = (
  countryName?: string | null
): { defaultLocale: PublicLocale; enabledLocales: PublicLocale[] } => {
  if (countryName && COUNTRY_DEFAULT_LOCALES[countryName]) {
    return COUNTRY_DEFAULT_LOCALES[countryName];
  }
  const cCode = getCountryCode(countryName);
  const def = `en-${cCode}` as PublicLocale;
  return {
    defaultLocale: def,
    enabledLocales: [def],
  };
};

export const getLocaleConfig = (locale: string | null | undefined) => {
  const norm = normalizePublicLocale(locale);
  const [lang, country] = norm.split('-');
  const langConfig = SUPPORTED_LANGUAGES.find(item => item.code === lang) || SUPPORTED_LANGUAGES[0];

  return {
    code: norm,
    hreflang: `${lang}-${(country || 'ma').toUpperCase()}`,
    language: langConfig.code,
    label: langConfig.label,
    nativeLabel: langConfig.nativeLabel,
    dir: langConfig.dir,
  };
};

export const getLocaleLanguage = (locale: string | null | undefined): Language =>
  getLocaleConfig(locale).language as Language;
